/**
 * Client-side Facebook Pixel tracking utilities
 * 
 * This module provides wrapper functions for browser-side fbq() tracking
 * with event deduplication support.
 */

/**
 * Check if Facebook Pixel is loaded
 */
function isFBPixelLoaded(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).fbq === 'function';
}

/**
 * Track a Facebook Pixel event with optional event_id for deduplication
 * 
 * @param eventName - Standard event name (e.g., 'Purchase', 'InitiateCheckout')
 * @param params - Event parameters
 * @param eventId - Optional event ID for deduplication with CAPI
 */
export function trackFBPixelEvent(
  eventName: string,
  params: Record<string, any> = {},
  eventId?: string
): void {
  try {
    if (!isFBPixelLoaded()) {
      console.log('[Facebook Pixel] fbq not loaded, skipping event:', eventName);
      return;
    }

    const fbq = (window as any).fbq;
    
    // If eventId provided, use it for deduplication
    if (eventId) {
      fbq('track', eventName, params, { eventID: eventId });
      console.log('[Facebook Pixel] Event tracked with deduplication:', eventName, eventId);
    } else {
      fbq('track', eventName, params);
      console.log('[Facebook Pixel] Event tracked:', eventName);
    }
  } catch (error) {
    console.error('[Facebook Pixel] Error tracking event:', error);
    // Never throw - graceful degradation
  }
}

/**
 * Track CompleteRegistration event (client-side)
 */
export function trackCompleteRegistrationPixel(
  email?: string,
  eventId?: string
): void {
  trackFBPixelEvent(
    'CompleteRegistration',
    {
      content_name: 'QuantWheel Email Signup',
      status: 'completed',
    },
    eventId
  );
}

/**
 * Track StartTrial event (client-side)
 */
export function trackStartTrialPixel(
  planType: string,
  eventId?: string
): void {
  trackFBPixelEvent(
    'StartTrial',
    {
      content_name: `QuantWheel ${planType} Trial`,
      predicted_ltv: 0,
      value: 0,
      currency: 'USD',
    },
    eventId
  );
}

/**
 * Track InitiateCheckout event (client-side)
 */
export function trackInitiateCheckoutPixel(
  planType: string,
  planValue: number,
  priceId?: string,
  eventId?: string
): void {
  trackFBPixelEvent(
    'InitiateCheckout',
    {
      content_name: `QuantWheel ${planType} Plan`,
      content_category: 'subscription',
      content_ids: [priceId || planType.toLowerCase()],
      content_type: 'product',
      value: planValue,
      currency: 'USD',
      num_items: 1,
    },
    eventId
  );
}

/**
 * Track Purchase event (client-side)
 */
export function trackPurchasePixel(
  planType: string,
  planValue: number,
  priceId?: string,
  eventId?: string
): void {
  trackFBPixelEvent(
    'Purchase',
    {
      content_name: `QuantWheel ${planType} Plan`,
      content_category: 'subscription',
      content_ids: [priceId || planType.toLowerCase()],
      content_type: 'product',
      value: planValue,
      currency: 'USD',
      num_items: 1,
    },
    eventId
  );
}

/**
 * Track Lead event (client-side)
 */
export function trackLeadPixel(
  contentName: string,
  eventId?: string
): void {
  trackFBPixelEvent(
    'HeroCTA',
    {
      content_name: contentName,
    },
    eventId
  );
}

/**
 * Track ViewContent event (client-side)
 */
export function trackViewContentPixel(
  contentName: string,
  contentCategory?: string,
  eventId?: string
): void {
  trackFBPixelEvent(
    'ViewContent',
    {
      content_name: contentName,
      content_category: contentCategory || 'page',
    },
    eventId
  );
}

